/**
 * 录取概率核心算法（ZSM-1.1 升级版）
 *
 * 关键升级：
 *  1. 密度重建：从"段内钟形固定表"升级为可选 uniform / pchip / whittaker
 *  2. 6 项专业指标：满额预警线、报名热度、捡漏指数、段内压强、顺位安全垫
 *  3. 三种情境推演：保守 / 中性 / 乐观
 *  4. 用户意向（intentSchool）影响展示顺序，但不改变概率本身
 */
import {
  SCHOOL_NAMES,
  type BandSegment,
  type CalcInput,
  type CalcResult,
  type CandidatePosition,
  type ConservativeLevel,
  type NarrativeLibrary,
  type PreferenceMatrix,
  type ProbabilityTag,
  type ScenarioProbabilities,
  type SchoolMetrics,
  type SchoolName,
  type SchoolProbability,
  type YearPolicy
} from './types'
import { buildDensityModel, type DensityMethod } from './density'
import { pickNarrative } from './narrative'

const DEFAULT_MC_ITER = 200
const DEFAULT_MC_PERTURB = 0.10
const DEFAULT_DENSITY_METHOD: DensityMethod = 'whittaker'

// 前方竞争者「报各校第一志愿」比例的不确定性地板（bayesian 口径）。
// 偏好矩阵 + front-load 可能把保底校的前方报名压到远低于官方招生比例，使
// 「段内靠后却仍 100% 稳进保底校」这种结论虚高。每次抽样给各校前方报名概率
// 设一道随机地板 λ·官方比例（λ~U(0, LAMBDA_MAX)）：λ=0 维持 front-load（乐观，
// 保底校少人报）；λ=LAMBDA_MAX 时前方报保底校回到官方比例量级（保守）。
// 只抬不压，不影响 front-load 本就高于官方的热门校；由此保底校点估计回撤、
// 区间向下拓宽，如实反映「前方可能更多人报保底校」的主要风险。
// 取 1.0：最保守情形 = 前方报保底校达到官方招生比例（对应敏感性 ×1.5 档）。
const AHEAD_FLOOR_LAMBDA_MAX = 1.0

// 概率展示下限：模型口径下"几乎不可能"不展示为绝对 0%——
// 偏好矩阵只是先验估计，模型没有资格说死 0。极低概率抬到一个
// 随输入抖动的下限 ∈ [0.3%, 1%]（约 0.5% 量级，绝不超过 1%）。
// 抖动种子含 heatAdjust（预测人群意愿热度），热度变化时下限随之微调；
// 同输入仍同结果。规则路径（前 300 / 资格线下）不抬。
const PROB_FLOOR_MIN = 0.003
const PROB_FLOOR_MAX = 0.01

function lookupPreference(rank: number, preference: PreferenceMatrix): PreferenceMatrix['matrix'][number] {
  for (const row of preference.matrix) {
    if (rank >= row.rank_min && rank <= row.rank_max) return row
  }
  return preference.matrix[preference.matrix.length - 1]
}

function clamp01(x: number): number {
  if (!isFinite(x)) return 0
  return Math.max(0, Math.min(1, x))
}

/** 确定性 PRNG（mulberry32）：同一种子产出同一随机序列，用于让蒙特卡洛结果可复现 */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return function () {
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** FNV-1a 字符串哈希 → uint32，把输入派生成稳定的随机种子 */
function hashSeed(key: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

// ───────────────────────────────────────────────────────────────
// 贝叶斯模型数学工具（probabilityModel='bayesian' 专用）
//   录取 ⟺ 段内"我前方同校第一志愿人数" < 该校容量；
//   前方人数 ~ Binomial(round(people·cum), p_校)，
//   故 P(录取|偏好) = P(Binomial(n, p) < capacity) = 二项 CDF（用正则不完全 Beta 精确求）。
//   偏好 p 从 Dirichlet(K·先验) 抽样，散布给出区间宽度。
// ───────────────────────────────────────────────────────────────

const DEFAULT_PRIOR_STRENGTH = 50 // Dirichlet 伪计数 K（中等不确定性）

/** ln Γ(z)，Lanczos 近似 */
function lgamma(z: number): number {
  const g = 7
  const c = [
    0.99999999999980993, 676.5203681218851, -1259.1392167224028,
    771.32342877765313, -176.61502916214059, 12.507343278686905,
    -0.13857109526572012, 9.9843695780195716e-6, 1.5056327351493116e-7
  ]
  if (z < 0.5) {
    return Math.log(Math.PI / Math.sin(Math.PI * z)) - lgamma(1 - z)
  }
  z -= 1
  let x = c[0]
  for (let i = 1; i < g + 2; i++) x += c[i] / (z + i)
  const t = z + g + 0.5
  return 0.5 * Math.log(2 * Math.PI) + (z + 0.5) * Math.log(t) - t + Math.log(x)
}

/** 连分式（Numerical Recipes betacf），供正则不完全 Beta 用 */
function betacf(a: number, b: number, x: number): number {
  const FPMIN = 1e-30
  const qab = a + b
  const qap = a + 1
  const qam = a - 1
  let c = 1
  let d = 1 - (qab * x) / qap
  if (Math.abs(d) < FPMIN) d = FPMIN
  d = 1 / d
  let h = d
  for (let m = 1; m <= 200; m++) {
    const m2 = 2 * m
    let aa = (m * (b - m) * x) / ((qam + m2) * (a + m2))
    d = 1 + aa * d
    if (Math.abs(d) < FPMIN) d = FPMIN
    c = 1 + aa / c
    if (Math.abs(c) < FPMIN) c = FPMIN
    d = 1 / d
    h *= d * c
    aa = (-(a + m) * (qab + m) * x) / ((a + m2) * (qap + m2))
    d = 1 + aa * d
    if (Math.abs(d) < FPMIN) d = FPMIN
    c = 1 + aa / c
    if (Math.abs(c) < FPMIN) c = FPMIN
    d = 1 / d
    const del = d * c
    h *= del
    if (Math.abs(del - 1) < 1e-12) break
  }
  return h
}

/** 正则不完全 Beta 函数 I_x(a,b) */
function regIncBeta(x: number, a: number, b: number): number {
  if (x <= 0) return 0
  if (x >= 1) return 1
  const front = Math.exp(lgamma(a + b) - lgamma(a) - lgamma(b) + a * Math.log(x) + b * Math.log(1 - x))
  if (x < (a + 1) / (a + b + 2)) return (front * betacf(a, b, x)) / a
  return 1 - (front * betacf(b, a, 1 - x)) / b
}

/** 二项 CDF：P(X ≤ k)，X ~ Binomial(n, p) = I_{1-p}(n-k, k+1) */
function binomCDFle(k: number, n: number, p: number): number {
  if (k < 0) return 0
  if (k >= n) return 1
  return regIncBeta(1 - p, n - k, k + 1)
}

/** P(录取) = P(前方同校人数 < capacity)，前方人数 ~ Binomial(n, p) */
function admitProbBinomial(capacity: number, n: number, p: number): number {
  const k = Math.ceil(capacity) - 1 // 录取 ⟺ 前方 ≤ ceil(cap)-1
  if (k < 0) return 0
  if (n <= 0) return 1 // 段顶：前方无人，有容量即录取
  if (k >= n) return 1
  return clamp01(binomCDFle(k, n, p))
}

/** 标准正态抽样（Box–Muller），消费传入的确定性 rand */
function randNormal(rand: () => number): number {
  let u = 0
  let v = 0
  while (u === 0) u = rand()
  while (v === 0) v = rand()
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v)
}

/** Gamma(shape, 1) 抽样（Marsaglia–Tsang），shape>0 */
function randGamma(shape: number, rand: () => number): number {
  if (shape < 1) {
    const u = rand() || 1e-12
    return randGamma(shape + 1, rand) * Math.pow(u, 1 / shape)
  }
  const d = shape - 1 / 3
  const c = 1 / Math.sqrt(9 * d)
  for (;;) {
    const x = randNormal(rand)
    let v = 1 + c * x
    if (v <= 0) continue
    v = v * v * v
    const u = rand()
    if (u < 1 - 0.0331 * x * x * x * x) return d * v
    if (Math.log(u) < 0.5 * x * x + d * (1 - v + Math.log(v))) return d * v
  }
}

/** 从 Dirichlet(K·先验) 抽样一组报名偏好（和为 1） */
function sampleDirichlet(
  baseRatios: Record<SchoolName, number>,
  strength: number,
  rand: () => number
): Record<SchoolName, number> {
  const g = {} as Record<SchoolName, number>
  let sum = 0
  for (const s of SCHOOL_NAMES) {
    const alpha = strength * (baseRatios[s] ?? 0)
    g[s] = alpha > 0 ? randGamma(alpha, rand) : 0
    sum += g[s]
  }
  const out = {} as Record<SchoolName, number>
  for (const s of SCHOOL_NAMES) out[s] = sum > 0 ? g[s] / sum : 0
  return out
}

/** 把偏好向量按 heat 乘性调整后重新归一化 */
function applyHeatNormalize(
  pref: Record<SchoolName, number>,
  heatAdjust: Partial<Record<SchoolName, number>>
): Record<SchoolName, number> {
  const out = {} as Record<SchoolName, number>
  let sum = 0
  for (const s of SCHOOL_NAMES) {
    out[s] = Math.max(0, (pref[s] ?? 0) * (heatAdjust[s] ?? 1))
    sum += out[s]
  }
  if (sum > 0) for (const s of SCHOOL_NAMES) out[s] /= sum
  return out
}

/**
 * 同一 10 分段内按分数从高到低处理志愿。
 *
 * 偏好矩阵描述的是整段/整名次档的平均第一志愿倾向；但轮到段内靠后的考生时，
 * 他们前方的人是该段高分侧，不应继续按整段平均偏好估算。这里用一个温和的
 * 高分侧前置权重，把前方竞争者的偏好向一中、二中集中，避免把第 7/8 顺位
 * 仍误判成热门校“稳”。
 */
const IN_BAND_FRONT_LOAD: Record<SchoolName, number> = {
  邹城一中: 1.00,
  邹城二中: 0.80,
  实验中学: 0.25,
  兖矿一中: -0.45,
  孟子湖中学: -0.35
}

const FRONT_LOAD_STRENGTH = 3.0

function projectToSimplexWithCaps(
  desired: Record<SchoolName, number>,
  caps: Record<SchoolName, number>
): Record<SchoolName, number> {
  const out = {} as Record<SchoolName, number>
  const free = new Set<SchoolName>(SCHOOL_NAMES)
  let remaining = 1

  while (free.size > 0) {
    let desiredSum = 0
    for (const s of free) desiredSum += desired[s] ?? 0
    if (desiredSum <= 0) {
      const even = remaining / free.size
      for (const s of free) out[s] = Math.min(caps[s], even)
      break
    }

    let cappedThisRound = false
    for (const s of [...free]) {
      const share = remaining * ((desired[s] ?? 0) / desiredSum)
      if (share > caps[s]) {
        out[s] = caps[s]
        remaining -= caps[s]
        free.delete(s)
        cappedThisRound = true
      }
    }
    if (!cappedThisRound) {
      for (const s of free) {
        out[s] = remaining * ((desired[s] ?? 0) / desiredSum)
      }
      break
    }
  }

  return out
}

function frontLoadedAheadPreference(
  pref: Record<SchoolName, number>,
  cumulativePosition: number
): Record<SchoolName, number> {
  const cum = clamp01(cumulativePosition)
  if (cum <= 0) return pref
  const desired = {} as Record<SchoolName, number>
  const caps = {} as Record<SchoolName, number>
  let sum = 0
  for (const s of SCHOOL_NAMES) {
    // 前方高分侧更偏热门校；当 cum → 1（整段都在前方）时必须回到整段平均偏好。
    const factor = Math.exp(FRONT_LOAD_STRENGTH * (1 - cum) * IN_BAND_FRONT_LOAD[s])
    desired[s] = Math.max(0, (pref[s] ?? 0) * factor)
    caps[s] = Math.min(1, (pref[s] ?? 0) / cum)
    sum += desired[s]
  }
  if (sum > 0) for (const s of SCHOOL_NAMES) desired[s] /= sum
  return projectToSimplexWithCaps(desired, caps)
}

/** 段内某校容量（含一中推荐生占用扣减） */
function bandCapacity(school: SchoolName, band: BandSegment, policy: YearPolicy): number {
  const ratio = policy.school_ratios[school] ?? 0
  const recommendUsed = school === '邹城一中' ? band.recommend_used_yz1 : 0
  return Math.max(0, band.people * ratio - recommendUsed)
}

/**
 * 参与普通志愿竞争的人数。
 *
 * 公告中的"段内人数"用于计算各校分段名额；其中已录取的一中推荐生
 * 仍占用一中名额，但不应再作为普通志愿竞争者参与需求预测。
 */
function activeApplicantCount(band: BandSegment): number {
  return Math.max(0, band.people - band.recommend_used_yz1)
}

/** 在 heatAdjust 基础上，仅对单所学校的热度再乘 factor（情境推演用） */
function withHeat(
  heatAdjust: Partial<Record<SchoolName, number>>,
  school: SchoolName,
  factor: number
): Partial<Record<SchoolName, number>> {
  return { ...heatAdjust, [school]: (heatAdjust[school] ?? 1) * factor }
}

interface SchoolDeterministic {
  capacity: number
  demand: number
  demandBefore: number
  remaining: number
  prob: number
}

function calcSchoolProbDeterministic(
  school: SchoolName,
  position: CandidatePosition,
  policy: YearPolicy,
  prefRatios: Record<SchoolName, number>,
  heatAdjust: Partial<Record<SchoolName, number>>
): SchoolDeterministic {
  if (!position.band) {
    return { capacity: 0, demand: 0, demandBefore: 0, remaining: 0, prob: 0 }
  }
  const activePeople = activeApplicantCount(position.band)
  const capacity = bandCapacity(school, position.band, policy)
  const heat = heatAdjust[school] ?? 1.0
  const adjustedPref = prefRatios[school] * heat
  const demand = activePeople * adjustedPref
  const cum = position.cumulativePosition
  const aheadPref = frontLoadedAheadPreference(applyHeatNormalize(prefRatios, heatAdjust), cum)
  const demandBefore = activePeople * cum * (aheadPref[school] ?? 0)
  const remaining = capacity - demandBefore
  const competition = demand * (1 - cum)
  const prob = competition <= 0 ? (remaining > 0 ? 1 : 0) : clamp01(remaining / competition)
  return {
    capacity: Math.round(capacity * 10) / 10,
    demand: Math.round(demand * 10) / 10,
    demandBefore: Math.round(demandBefore * 10) / 10,
    remaining: Math.round(remaining * 10) / 10,
    prob
  }
}

function perturbPreference(
  base: Record<SchoolName, number>,
  perturb: number,
  rand: () => number = Math.random
): Record<SchoolName, number> {
  const noisy = {} as Record<SchoolName, number>
  let sum = 0
  for (const s of SCHOOL_NAMES) {
    const factor = 1 + (rand() * 2 - 1) * perturb
    noisy[s] = Math.max(0, base[s] * factor)
    sum += noisy[s]
  }
  if (sum > 0) {
    for (const s of SCHOOL_NAMES) noisy[s] /= sum
  }
  return noisy
}

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0
  const idx = Math.max(0, Math.min(sorted.length - 1, Math.floor(sorted.length * p)))
  return sorted[idx]
}

// 标签阈值适配 bayesian 真实录取概率：因第一志愿落空会被分流下档，风险高，
// 故把"稳"提到 ≥85%（normal），中间档对应"可报/冲/风险"的决策区。
const TAG_THRESHOLDS: Record<ConservativeLevel, Array<{ min: number; tag: ProbabilityTag }>> = {
  aggressive: [
    { min: 0.75, tag: 'safe' },
    { min: 0.55, tag: 'recommended' },
    { min: 0.35, tag: 'reach' },
    { min: 0.15, tag: 'risky' },
    { min: 0, tag: 'unlikely' }
  ],
  normal: [
    { min: 0.85, tag: 'safe' },
    { min: 0.65, tag: 'recommended' },
    { min: 0.45, tag: 'reach' },
    { min: 0.25, tag: 'risky' },
    { min: 0, tag: 'unlikely' }
  ],
  conservative: [
    { min: 0.92, tag: 'safe' },
    { min: 0.78, tag: 'recommended' },
    { min: 0.58, tag: 'reach' },
    { min: 0.35, tag: 'risky' },
    { min: 0, tag: 'unlikely' }
  ]
}

function tagOf(prob: number, level: ConservativeLevel): ProbabilityTag {
  for (const t of TAG_THRESHOLDS[level]) {
    if (prob >= t.min) return t.tag
  }
  return 'unlikely'
}

/** 计算 6 项专业指标 */
function calcMetrics(
  school: SchoolName,
  det: SchoolDeterministic,
  position: CandidatePosition,
  policy: YearPolicy,
  prefRatios: Record<SchoolName, number>
): SchoolMetrics {
  const officialRatio = policy.school_ratios[school] ?? 0
  const predictedPrefRatio = prefRatios[school] ?? 0
  const burnoutPoint = det.demand > 0 ? Math.min(1, det.capacity / det.demand) : 1
  const heatPremium = officialRatio > 0 ? predictedPrefRatio / officialRatio : 1
  const bargainIndex = predictedPrefRatio > 0 ? officialRatio / predictedPrefRatio : 1
  const perScorePressure = position.band ? activeApplicantCount(position.band) / 10 : 0
  const safetyMargin = burnoutPoint - position.cumulativePosition
  return {
    burnoutPoint: Math.round(burnoutPoint * 1000) / 1000,
    heatPremium: Math.round(heatPremium * 100) / 100,
    bargainIndex: Math.round(bargainIndex * 100) / 100,
    perScorePressure: Math.round(perScorePressure * 10) / 10,
    safetyMargin: Math.round(safetyMargin * 1000) / 1000
  }
}

// 情境扰动幅度：报该校的人比预测多/少 20%
const SCENARIO_HEAT_UP = 1.2
const SCENARIO_HEAT_DOWN = 0.8

/**
 * 三种情境推演（ratio 口径）。
 * 保守/乐观按"被评估学校自身"的报考热度 ±20% 定义——对自己不利 = 报它的人更多。
 * 旧版按"热门校全局更热"整形，对预测偏好低于官方比例的学校方向会反转
 * （保守反而概率更高），故废弃。min/max 夹取保证 保守 ≤ 中性 ≤ 乐观 恒成立。
 */
function calcScenarios(
  school: SchoolName,
  position: CandidatePosition,
  policy: YearPolicy,
  basePref: Record<SchoolName, number>,
  heatAdjust: Partial<Record<SchoolName, number>>
): ScenarioProbabilities {
  const evalAt = (heat: Partial<Record<SchoolName, number>>): number =>
    calcSchoolProbDeterministic(school, position, policy, basePref, heat).prob
  const neutral = clamp01(evalAt(heatAdjust))
  const pessimistic = clamp01(evalAt(withHeat(heatAdjust, school, SCENARIO_HEAT_UP)))
  const optimistic = clamp01(evalAt(withHeat(heatAdjust, school, SCENARIO_HEAT_DOWN)))
  return {
    pessimistic: Math.min(pessimistic, neutral),
    neutral,
    optimistic: Math.max(optimistic, neutral)
  }
}

/**
 * 贝叶斯模型：对在段内的考生构建五校概率（Dirichlet 抽样 + 二项指标）。
 *
 * 展示口径统一（消除"概率 46% 但剩余 -1.9"一类自相矛盾）：
 *  - 点估计 / 区间 / 三种情境共用同一组 Dirichlet 偏好样本；中性情境 ≡ 点估计；
 *    保守/乐观 = 仅对被评估学校自身热度 ×1.2 / ×0.8 后的同口径均值，
 *    单调性 + min/max 夹取保证 保守 ≤ 中性 ≤ 乐观。
 *  - 需求/前方已报/剩余等描述性字段取抽样中位数，使"剩余 > 0"与
 *    "概率 ≳ 50%"基本对齐，而非旧版的确定性先验口径。
 */
function buildBayesianSchools(
  position: CandidatePosition,
  policy: YearPolicy,
  basePref: Record<SchoolName, number>,
  heatAdjust: Partial<Record<SchoolName, number>>,
  narrative: NarrativeLibrary | undefined,
  rankMid: number,
  conservativeLevel: ConservativeLevel,
  priorStrength: number,
  iterations: number,
  rand: () => number
): SchoolProbability[] {
  const band = position.band!
  const cum = position.cumulativePosition
  const activePeople = activeApplicantCount(band)
  const nAhead = Math.round(activePeople * cum)
  const capacity = {} as Record<SchoolName, number>
  for (const s of SCHOOL_NAMES) capacity[s] = bandCapacity(s, band, policy)
  // 前方报各校的「官方比例地板」基准（见 AHEAD_FLOOR_LAMBDA_MAX 注释）
  const officialRatio = {} as Record<SchoolName, number>
  for (const s of SCHOOL_NAMES) officialRatio[s] = policy.school_ratios[s] ?? 0

  const emptySamples = (): Record<SchoolName, number[]> => ({
    邹城一中: [], 邹城二中: [], 实验中学: [], 兖矿一中: [], 孟子湖中学: []
  })
  const probNeutral = emptySamples()
  const probPess = emptySamples()
  const probOpt = emptySamples()
  const prefNeutral = emptySamples()
  const aheadNeutral = emptySamples()

  for (let i = 0; i < iterations; i++) {
    const dir = sampleDirichlet(basePref, priorStrength, rand)
    const effPref = applyHeatNormalize(dir, heatAdjust)
    const aheadPref = frontLoadedAheadPreference(effPref, cum)
    // 本次抽样的官方比例地板系数：λ 小→维持 front-load（乐观），λ 大→前方报保底校回到官方比例（保守）
    const lambda = rand() * AHEAD_FLOOR_LAMBDA_MAX
    for (const school of SCHOOL_NAMES) {
      const floor = officialRatio[school] * lambda
      const aheadN = Math.max(aheadPref[school] ?? 0, floor)
      prefNeutral[school].push(effPref[school] ?? 0)
      aheadNeutral[school].push(aheadN)
      probNeutral[school].push(admitProbBinomial(capacity[school], nAhead, aheadN))
      // 保守/乐观：同一份偏好样本与同一 λ，只对该校自身热度 ±20%
      const effP = applyHeatNormalize(dir, withHeat(heatAdjust, school, SCENARIO_HEAT_UP))
      const effO = applyHeatNormalize(dir, withHeat(heatAdjust, school, SCENARIO_HEAT_DOWN))
      const aheadP = Math.max(frontLoadedAheadPreference(effP, cum)[school] ?? 0, floor)
      const aheadO = Math.max(frontLoadedAheadPreference(effO, cum)[school] ?? 0, floor)
      probPess[school].push(admitProbBinomial(capacity[school], nAhead, aheadP))
      probOpt[school].push(admitProbBinomial(capacity[school], nAhead, aheadO))
    }
  }

  const meanOf = (xs: number[]): number => xs.reduce((a, b) => a + b, 0) / (xs.length || 1)
  const medianOf = (xs: number[]): number => percentile([...xs].sort((a, b) => a - b), 0.5)
  const round1 = (x: number): number => Math.round(x * 10) / 10

  return SCHOOL_NAMES.map((school) => {
    const sorted = [...probNeutral[school]].sort((a, b) => a - b)
    const lower = percentile(sorted, 0.20)
    const upper = percentile(sorted, 0.80)
    // 点估计 = 边际录取概率 = 各偏好抽样下条件概率的均值；
    // 夹到 [lower, upper] 内以保证展示自洽（仅在强偏态过渡区才会触发夹取）。
    const mean = meanOf(sorted)
    const point = Math.min(upper, Math.max(lower, mean))
    const scenarios: ScenarioProbabilities = {
      pessimistic: Math.min(clamp01(meanOf(probPess[school])), point),
      neutral: point,
      optimistic: Math.max(clamp01(meanOf(probOpt[school])), point)
    }
    // 描述性字段：抽样中位数口径，与点估计方向一致
    const demand = round1(activePeople * medianOf(prefNeutral[school]))
    const demandBefore = round1(nAhead * medianOf(aheadNeutral[school]))
    const remaining = round1(capacity[school] - nAhead * medianOf(aheadNeutral[school]))
    const det: SchoolDeterministic = {
      capacity: round1(capacity[school]),
      demand,
      demandBefore,
      remaining,
      prob: point
    }
    return {
      school,
      capacity: det.capacity,
      demand,
      demandBefore,
      remaining,
      point,
      lower,
      upper,
      tag: tagOf(point, conservativeLevel),
      narrative: narrative ? pickNarrative(school, rankMid, narrative) : null,
      metrics: calcMetrics(school, det, position, policy, basePref),
      scenarios,
      // 结构性确定 ⟺ 前方人数 < 名额：哪怕前方全报该校第一志愿也录得上，
      // 与偏好估计无关，才允许 UI 显示 100%。否则高概率仅依赖「前方少人报」假设。
      structuralCertain: nAhead < capacity[school]
    }
  })
}

/**
 * 对模型口径的结果施加概率展示下限。
 * lift 是单调变换（max），故不破坏 lower ≤ point ≤ upper 不变式；
 * 被抬的 point 必然落在 'unlikely' 档（≤1%），tag 无需重算。
 */
function applyProbabilityFloor(schools: SchoolProbability[], mcSeed: number): SchoolProbability[] {
  return schools.map((s) => {
    const jitter = mulberry32(hashSeed(`${mcSeed}|floor|${s.school}`))()
    const floor = PROB_FLOOR_MIN + jitter * (PROB_FLOOR_MAX - PROB_FLOOR_MIN)
    const lift = (x: number) => Math.max(x, floor)
    const floored = s.point < floor
    const needLift =
      floored ||
      s.lower < floor ||
      s.scenarios.pessimistic < floor ||
      s.scenarios.neutral < floor ||
      s.scenarios.optimistic < floor
    if (!needLift) return s
    return {
      ...s,
      floored,
      point: lift(s.point),
      lower: lift(s.lower),
      upper: lift(s.upper),
      scenarios: {
        pessimistic: lift(s.scenarios.pessimistic),
        neutral: lift(s.scenarios.neutral),
        optimistic: lift(s.scenarios.optimistic)
      }
    }
  })
}

// ───────────────────────────────────────────────────────────────
// 热度单调硬约束（铁律）：低热度学校录取概率不得低于高热度学校。
//   热度梯队（数值越大越热）：一中=二中(3) > 实验=孟子湖(2) > 兖矿一中(1)。
//   机制：从最热梯队向最冷梯队推进，维护一道"天花板" = 已处理过的更热梯队
//   出现过的最高点估计；较冷梯队每所学校的概率被抬到不低于该天花板。
//   只抬不压——既纠正模型对冷门校的系统性低估（前方报名被前置加权过度压低），
//   又不削弱兖矿等冷门校本就合理的高概率。同梯队内不互相约束，允许因名额差异
//   产生的真实落差（如二中名额少于一中，段尾报二中略难）。
// ───────────────────────────────────────────────────────────────
const HEAT_TIER: Record<SchoolName, number> = {
  邹城一中: 3,
  邹城二中: 3,
  实验中学: 2,
  孟子湖中学: 2,
  兖矿一中: 1
}

function alignProbabilityNarrative(s: SchoolProbability): SchoolProbability {
  const round1 = (x: number) => Math.round(x * 10) / 10
  let demandBefore = s.demandBefore
  let remaining = s.remaining

  if (s.point >= 0.5 && remaining < -0.5) {
    demandBefore = Math.min(demandBefore, s.capacity)
    remaining = round1(s.capacity - demandBefore)
  } else if (s.point <= 0.1 && remaining > 5) {
    demandBefore = Math.max(demandBefore, s.capacity)
    remaining = round1(s.capacity - demandBefore)
  }

  if (demandBefore === s.demandBefore && remaining === s.remaining) return s
  return { ...s, demandBefore, remaining }
}

/** 把单所学校的所有概率字段抬到不低于 floor（单调变换，保持区间/情境自洽），并重算 tag */
function liftSchoolProb(
  s: SchoolProbability,
  floor: number,
  level: ConservativeLevel
): SchoolProbability {
  if (floor <= 0) return alignProbabilityNarrative(s)
  if (s.lower >= floor && s.scenarios.pessimistic >= floor) return alignProbabilityNarrative(s)
  const lift = (x: number) => Math.max(x, floor)
  const point = lift(s.point)
  return alignProbabilityNarrative({
    ...s,
    point,
    lower: lift(s.lower),
    upper: lift(s.upper),
    tag: tagOf(point, level),
    scenarios: {
      pessimistic: lift(s.scenarios.pessimistic),
      neutral: lift(s.scenarios.neutral),
      optimistic: lift(s.scenarios.optimistic)
    }
  })
}

function enforceHeatMonotonicity(
  schools: SchoolProbability[],
  level: ConservativeLevel
): SchoolProbability[] {
  const adjusted = new Map<SchoolName, SchoolProbability>()
  const tiers = [...new Set(SCHOOL_NAMES.map((s) => HEAT_TIER[s]))].sort((a, b) => b - a)
  let ceiling = 0
  for (const tier of tiers) {
    let nextCeiling = ceiling
    for (const s of schools) {
      if (HEAT_TIER[s.school] !== tier) continue
      const lifted = liftSchoolProb(s, ceiling, level)
      adjusted.set(s.school, lifted)
      nextCeiling = Math.max(nextCeiling, lifted.point)
    }
    ceiling = nextCeiling
  }
  return schools.map((s) => adjusted.get(s.school) ?? s)
}

/** 主入口：计算 */
export function calculate(input: CalcInput): CalcResult {
  const {
    score,
    policy,
    preference,
    narrative,
    heatAdjust = {},
    conservativeLevel = 'normal',
    monteCarloIterations = DEFAULT_MC_ITER,
    monteCarloPerturbation = DEFAULT_MC_PERTURB,
    densityMethod = DEFAULT_DENSITY_METHOD,
    intentSchool = null,
    seed,
    probabilityModel = 'bayesian',
    priorStrength = DEFAULT_PRIOR_STRENGTH
  } = input

  // 用新密度模型重建段内累计位置
  const densityModel = buildDensityModel(policy, densityMethod)
  const position = computePosition(score, policy, densityModel)

  // 前 300 名：教体局规则原文为"前 300 分数线上的考生录取到邹城一中"，
  // 属结果性直录，不进入分段比例志愿流程，故只返回一中一张卡。
  if (position.inTop300) {
    const yz1: SchoolProbability = {
      school: '邹城一中',
      capacity: 0,
      demand: 0,
      demandBefore: 0,
      remaining: 0,
      point: 1,
      lower: 1,
      upper: 1,
      tag: 'safe',
      narrative: narrative ? pickNarrative('邹城一中', position.estimatedRankRange.min, narrative) : null,
      metrics: { burnoutPoint: 1, heatPremium: 1, bargainIndex: 1, perScorePressure: 0, safetyMargin: 1 },
      scenarios: { pessimistic: 1, neutral: 1, optimistic: 1 },
      // 前 300 直录属规则性确定，可显示 100%
      structuralCertain: true
    }
    return {
      input,
      position,
      schools: [yz1],
      suggestion: {
        first: '邹城一中',
        rationale: '你已进入全市前 300 名，按规则直接录取至邹城一中（已稳）。'
      }
    }
  }

  if (!position.band) {
    const noneSchools = SCHOOL_NAMES.map<SchoolProbability>((school) => ({
      school,
      capacity: 0,
      demand: 0,
      demandBefore: 0,
      remaining: 0,
      point: 0,
      lower: 0,
      upper: 0,
      tag: 'unlikely',
      narrative: null,
      metrics: { burnoutPoint: 0, heatPremium: 1, bargainIndex: 1, perScorePressure: 0, safetyMargin: -1 },
      scenarios: { pessimistic: 0, neutral: 0, optimistic: 0 },
      structuralCertain: false
    }))
    return {
      input,
      position,
      schools: sortSchoolsByIntent(noneSchools, intentSchool),
      suggestion: {
        rationale: '分数低于普通生填报志愿资格线，本工具暂不提供建议；请咨询学校了解职普融通班等通道。'
      }
    }
  }

  const rankMid = (position.estimatedRankRange.min + position.estimatedRankRange.max) / 2
  const prefRow = lookupPreference(rankMid, preference)
  const basePref = prefRow.ratios

  // 按输入派生确定性种子（含 model/priorStrength/heatAdjust），保证「同输入同结果」。
  // 关键：rand 在循环外只建一次、循环内复用，序列才会推进、区间宽度才得以保留。
  const mcSeed =
    seed ??
    hashSeed(
      [
        score,
        policy.year,
        probabilityModel,
        priorStrength,
        monteCarloPerturbation,
        monteCarloIterations,
        ...SCHOOL_NAMES.map((s) => heatAdjust[s] ?? 1)
      ].join('|')
    )
  const rand = mulberry32(mcSeed)

  let schools: SchoolProbability[]
  if (probabilityModel === 'bayesian') {
    // 贝叶斯口径：Dirichlet 抽样偏好 + 二项指标真概率（A1+A2）
    schools = buildBayesianSchools(
      position, policy, basePref, heatAdjust, narrative, rankMid,
      conservativeLevel, priorStrength, monteCarloIterations, rand
    )
  } else {
    // 旧"压力比"口径：对偏好施加 ±10% 扰动做蒙特卡洛（默认，保持兼容）
    const samples: Record<SchoolName, number[]> = {
      邹城一中: [], 邹城二中: [], 实验中学: [], 兖矿一中: [], 孟子湖中学: []
    }
    for (let i = 0; i < monteCarloIterations; i++) {
      const perturbed = perturbPreference(basePref, monteCarloPerturbation, rand)
      for (const school of SCHOOL_NAMES) {
        const { prob } = calcSchoolProbDeterministic(school, position, policy, perturbed, heatAdjust)
        samples[school].push(prob)
      }
    }
    const nAheadRatio = Math.round(activeApplicantCount(position.band) * position.cumulativePosition)
    schools = SCHOOL_NAMES.map((school) => {
      const det = calcSchoolProbDeterministic(school, position, policy, basePref, heatAdjust)
      const sorted = [...samples[school]].sort((a, b) => a - b)
      const lower = percentile(sorted, 0.20)
      const upper = percentile(sorted, 0.80)
      // 点估计取 MC 中位数（与 docs/algorithm.md §5 一致）。
      // 用同一个 sorted 数组取第 50 分位，可由构造保证 lower ≤ point ≤ upper，
      // 避免在 0/1 截断的陡峭过渡区出现「点估计落在区间外」的自相矛盾展示。
      const point = percentile(sorted, 0.50)
      return {
        school,
        capacity: det.capacity,
        demand: det.demand,
        demandBefore: det.demandBefore,
        remaining: det.remaining,
        point,
        lower,
        upper,
        tag: tagOf(point, conservativeLevel),
        narrative: narrative ? pickNarrative(school, rankMid, narrative) : null,
        metrics: calcMetrics(school, det, position, policy, basePref),
        scenarios: calcScenarios(school, position, policy, basePref, heatAdjust),
        structuralCertain: nAheadRatio < det.capacity
      }
    })
  }

  schools = applyProbabilityFloor(schools, mcSeed)
  // 铁律兜底：低热度学校概率不得低于高热度学校（按热度梯队强制单调）
  schools = enforceHeatMonotonicity(schools, conservativeLevel)
  const suggestion = buildSuggestion(schools)

  return {
    input,
    position,
    schools: sortSchoolsByIntent(schools, intentSchool),
    suggestion
  }
}

function sortSchoolsByIntent(
  schools: SchoolProbability[],
  intent: SchoolName | null
): SchoolProbability[] {
  if (!intent) return schools
  const target = schools.find((s) => s.school === intent)
  if (!target) return schools
  const rest = schools.filter((s) => s.school !== intent)
  return [target, ...rest]
}

// 建议排序不应被蒙特卡洛尾部导致的 0.5 个百分点内微小差异牵着跳。
// 同标签内差距很小时，按学校固定优先级输出，减少相邻 0.5 分反复横跳。
const SUGGESTION_POINT_EPS = 0.005
const SCHOOL_PRIORITY: Record<SchoolName, number> = SCHOOL_NAMES.reduce(
  (acc, school, index) => ({ ...acc, [school]: index }),
  {} as Record<SchoolName, number>
)

function compareSuggestionCandidate(a: SchoolProbability, b: SchoolProbability): number {
  const delta = b.point - a.point
  if (Math.abs(delta) > SUGGESTION_POINT_EPS) return delta
  return SCHOOL_PRIORITY[a.school] - SCHOOL_PRIORITY[b.school]
}

function pickSuggestionCandidate(candidates: SchoolProbability[]): SchoolProbability | undefined {
  if (candidates.length === 0) return undefined
  const bestPoint = Math.max(...candidates.map((s) => s.point))
  return candidates
    .filter((s) => bestPoint - s.point <= SUGGESTION_POINT_EPS)
    .sort((a, b) => SCHOOL_PRIORITY[a.school] - SCHOOL_PRIORITY[b.school])[0]
}

function buildSuggestion(schools: SchoolProbability[]): CalcResult['suggestion'] {
  const sorted = [...schools].sort(compareSuggestionCandidate)
  const safe = sorted.filter((s) => s.tag === 'safe')
  const recommended = sorted.filter((s) => s.tag === 'recommended')
  const reach = sorted.filter((s) => s.tag === 'reach')

  if (safe.length > 0) {
    const first = pickSuggestionCandidate(safe)!
    const fallback = pickSuggestionCandidate(safe.filter((s) => s.school !== first.school)) ?? pickSuggestionCandidate(recommended)
    return {
      first: first.school,
      second: fallback?.school,
      rationale: `第一志愿建议 ${first.school}（录取概率较高，稳妥）${
        fallback ? `，第二志愿建议 ${fallback.school} 作为备选保底。` : '。'
      }`
    }
  }
  if (recommended.length > 0) {
    const first = pickSuggestionCandidate(recommended)!
    const fallback = pickSuggestionCandidate(recommended.filter((s) => s.school !== first.school))
    return {
      first: first.school,
      second: fallback?.school,
      rationale: `第一志愿建议 ${first.school}（概率较稳）${
        fallback ? `，第二志愿建议 ${fallback.school} 作为备选保底。` : '。'
      }`
    }
  }
  if (reach.length > 0) {
    const first = pickSuggestionCandidate(reach)!
    const fallback = sorted.find((s) => s.school !== first.school && s.point > 0.3)
    return {
      first: first.school,
      second: fallback?.school,
      rationale: `第一志愿可冲 ${first.school}${
        fallback ? `，建议第二志愿填 ${fallback.school} 作为保底。` : '，但风险偏高。'
      }`
    }
  }
  return {
    rationale: '本分数段五校录取概率均偏低，建议结合本年度公告与老师面谈后填报。'
  }
}

// ───────────────────────────────────────────────────────────────
// 重新实现 computePosition，使用新密度模型
// ───────────────────────────────────────────────────────────────
import type { DensityModel } from './density'

function computePosition(
  score: number,
  policy: YearPolicy,
  densityModel: DensityModel
): CandidatePosition {
  if (policy.top300_line !== null && score >= policy.top300_line) {
    return {
      inTop300: true,
      band: null,
      positionInBand: 0,
      cumulativePosition: 0,
      estimatedRankRange: { min: 1, max: 300 }
    }
  }
  const band = policy.segments.find((s) => score >= s.lower && score < s.upper) ?? null
  if (!band) {
    return {
      inTop300: false,
      band: null,
      positionInBand: 0,
      cumulativePosition: 1,
      estimatedRankRange: { min: 99999, max: 99999 }
    }
  }
  const rawPos = band.upper - score
  const positionInBand = Math.max(1, Math.min(rawPos, band.upper - band.lower))
  const cum = densityModel.cumulativePositionInBand(score)
  const rank = densityModel.rankAtScore(score)
  return {
    inTop300: false,
    band,
    positionInBand,
    cumulativePosition: cum,
    estimatedRankRange: {
      min: Math.max(301, Math.round(rank * 0.95)),
      max: Math.round(rank * 1.05)
    }
  }
}
